import type { SeverityLevel } from '@sentry/vue'
import * as Sentry from '@sentry/vue'
import type { User } from '~/types/user'
import type { GenericObject, StringObject, UnknownObject } from '~/types/common'
import type { Licence } from '~/types/licence'
import { useMonitoringContext } from './internal/context'

interface UseSentryReturn {
  setUserContext: (userData: User) => void
  updateLicenceContext: (licence: Licence) => void
  clearUserContext: () => void
  addBreadcrumb: (message: string, category: string, data?: StringObject) => void
  captureException: (error: Error, context?: GenericObject, level?: SeverityLevel) => void
}

export const useSentry = (): UseSentryReturn => {
  const authStore = useAuthStore()

  const setUserContext = (userData: User): void => {
    Sentry.setUser({
      id: userData.id,
      email: userData.email,
      roles: userData.roles.map((role) => role.name).join(',')
    })

    if (authStore.activeLicence) {
      updateLicenceContext(authStore.activeLicence)
    }
  }

  const updateLicenceContext = (licence: Licence): void => {
    Sentry.setContext('licence', {
      id: licence.id,
      name: licence.name
    })
  }

  const clearUserContext = (): void => {
    Sentry.setUser(null)
  }

  const addCrumb = (category: string, message: string, data?: UnknownObject): void => {
    const { getCrumbData } = useMonitoringContext()
    Sentry.addBreadcrumb({
      category,
      message,
      level: 'info',
      timestamp: Date.now() / 1000,
      data: getCrumbData(data)
    })
  }

  const addBreadcrumb = (message: string, category: string, data?: StringObject): void => {
    addCrumb(category, message, data)
  }

  const captureException = (error: Error, context?: GenericObject, level: SeverityLevel = 'error'): void => {
    const { getBaseContext } = useMonitoringContext()
    Sentry.withScope((scope) => {
      const base = getBaseContext()

      scope.setContext('base', base)

      if (context) scope.setContext('error_context', context)
      scope.setTag('error.handled', true)
      scope.setLevel(level)
      Sentry.captureException(error)
    })
  }

  return {
    setUserContext,
    updateLicenceContext,
    clearUserContext,
    addBreadcrumb,
    captureException
  }
}
